import { AfterRenderRef, Component, DestroyRef, ErrorHandler, Inject, Injector, OnDestroy, OnInit, SecurityContext, afterNextRender, computed, inject, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { OktaAuth } from '@okta/okta-auth-js';
import { commonUtil, Lux } from '@roosevelt/common-ui-lib/core';
import { MagnoliaService } from 'src/app/magnolia/magnolia.service';
import { from } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  luxId = 'BMT-login' + this.constructor.name;
  readonly showSignIn = signal(true);
  readonly showRegistration = signal(false);
  readonly isFirstTimeLogin = signal(false);
  userTypes = [
    { value: null, label: '' },
    { value: 'admin', label: 'Administrator' },
    { value: 'tpa', label: 'Third-party Administrator' },
    { value: 'agency', label: 'Agency' },
    { value: 'agent', label: 'Agent' },
    { value: 'producer', label: 'Producer' }
  ];
  renPlan: boolean;
  private loginComponentDestroyed = false;
  private isRecoveringTransaction = false;
  private recoverableSignInRemountRef: AfterRenderRef | null = null;
  private readonly recoverableTransactionErrorText = 'No `step` or `action` provided';
  private readonly recoverableTransactionClearOptions = {
    clearSharedStorage: true,
    clearIdxResponse: true
  };
  private readonly preventSignInVisibilityReloadHandler = (event: Event): void => {
    if (!this.showSignIn()) {
      return;
    }

    // Keep the sign-in widget state (for example typed username) when users switch tabs.
    event.stopImmediatePropagation();
  };
  private readonly unhandledRejectionHandler = (event: PromiseRejectionEvent): void => {
    if (!this.isRecoverableLoginTransactionError(event.reason)) {
      return;
    }

    event.preventDefault();
    this.recoverRecoverableLoginTransaction();
  };
  private readonly loginPageContentRaw = toSignal(this.magnoliaService.getMagnoliaContent('login'), { initialValue: '' });
  readonly loginPageContent = computed<string | null>(() => {
    const content = this.loginPageContentRaw();
    return content ? this.sanitizer.sanitize(SecurityContext.HTML, content) : null;
  });

  constructor(
    @Inject(DOCUMENT) private readonly docRef: Document,
    private readonly magnoliaService: MagnoliaService,
    private readonly sanitizer: DomSanitizer,
    private readonly lux: Lux<{}>,
    private readonly router: Router,
    private readonly oktaAuth: OktaAuth,
    private readonly errorHandler: ErrorHandler
  ) {
    this.renPlan = docRef.location.pathname.split('/')[1].toLowerCase() === 'ren';
  }

  ngOnDestroy(): void {
    this.loginComponentDestroyed = true;
    this.recoverableSignInRemountRef?.destroy();
    this.recoverableSignInRemountRef = null;
    this.docRef.removeEventListener('visibilitychange', this.preventSignInVisibilityReloadHandler, true);
    this.docRef.defaultView?.removeEventListener('unhandledrejection', this.unhandledRejectionHandler);
    this.lux.destroy(this.luxId);
  }

  ngOnInit(): void {
    this.docRef.addEventListener('visibilitychange', this.preventSignInVisibilityReloadHandler, true);
    this.docRef.defaultView?.addEventListener('unhandledrejection', this.unhandledRejectionHandler);
    this.resolveAuthenticationStatus();
  }

  firstTimeLogin(): void {
    this.isFirstTimeLogin.set(true);
  }

  private resolveAuthenticationStatus(): void {
    from(commonUtil.authentication.status(this.oktaAuth)).pipe(
      switchMap(() => from(this.router.navigateByUrl('/oidc-callback'))),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      error: error => {
        if (this.isRecoverableLoginTransactionError(error)) {
          this.recoverRecoverableLoginTransaction();
          return;
        }

        this.errorHandler.handleError(error);
      }
    });
  }

  private isRecoverableLoginTransactionError(error: unknown): boolean {
    return error instanceof Error && error.message.includes(this.recoverableTransactionErrorText);
  }

  private recoverRecoverableLoginTransaction(): void {
    if (this.isRecoveringTransaction) {
      return;
    }

    this.isRecoveringTransaction = true;
    this.oktaAuth.transactionManager.clear(this.recoverableTransactionClearOptions);
    this.showSignIn.set(false);

    this.recoverableSignInRemountRef?.destroy();
    this.recoverableSignInRemountRef = afterNextRender(() => {
      this.recoverableSignInRemountRef = null;
      if (!this.loginComponentDestroyed) {
        this.showSignIn.set(true);
      }
      this.isRecoveringTransaction = false;
    }, { injector: this.injector });
  }

}
